import { Component, OnInit, QueryList, signal, ViewChildren } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, firstValueFrom, forkJoin, shareReplay } from 'rxjs';
import { NbToastrService } from '@nebular/theme';
import {
  BuildingEquipmentDetailDto,
  BuildingEquipmentMergeDto,
  BuildingEquipmentService,
  CustomFieldCollectionDetailDto,
  CustomFieldCollectionInstanceDetailDto,
  CustomFieldCollectionService,
  EquipmentService,
} from '@artemis-software/wr-api';
import { CustomFormComponent } from '@shared/custom-form/custom-form/custom-form.component';
import { validateCustomForms } from '@shared/custom-form/custom-form-submit';
import { PendingChanges } from '@guards/pending-changes.guard';

export interface BuildingEquipmentFieldSection {
  collectionDetail: CustomFieldCollectionDetailDto;
  existingInstance: CustomFieldCollectionInstanceDetailDto | undefined;
  expanded: boolean;
}

@Component({
  standalone: false,
  selector: 'wr-building-equipment-fields-tab',
  templateUrl: './building-equipment-fields-tab.component.html',
  styleUrls: ['./building-equipment-fields-tab.component.scss'],
})
export class BuildingEquipmentFieldsTabComponent implements OnInit, PendingChanges {

  @ViewChildren(CustomFormComponent)
  customForms!: QueryList<CustomFormComponent>;

  readonly loading$ = new BehaviorSubject(true);
  readonly saving$ = new BehaviorSubject(false);
  readonly isEditing = signal(false);
  readonly sections = signal<BuildingEquipmentFieldSection[]>([]);

  private buildingEquipmentId: string | null = null;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly buildingEquipmentService: BuildingEquipmentService,
    private readonly equipmentService: EquipmentService,
    private readonly collectionService: CustomFieldCollectionService,
    private readonly toastrService: NbToastrService,
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const id = this.buildingEquipmentId = params.get('id');
      if (id && id !== 'new') {
        this.loadData(id);
      } else {
        this.loading$.next(false);
      }
    });
  }

  async loadData(id: string): Promise<void> {
    try {
      this.loading$.next(true);
      const dto: BuildingEquipmentDetailDto = await firstValueFrom(
        this.buildingEquipmentService.findById(id),
      );

      const equipmentCollections = await firstValueFrom(
        this.equipmentService.findEquipmentCustomFieldCollectionByEquipmentId(dto.equipment.id),
      );
      const sorted = equipmentCollections.sort((a, b) => a.ranking - b.ranking);

      if (!sorted.length) {
        this.sections.set([]);
        return;
      }

      const detailObservables = sorted.map(ec =>
        this.collectionService.findById(ec.collection.id).pipe(shareReplay(1)),
      );
      const details = await firstValueFrom(forkJoin(detailObservables));

      const sections: BuildingEquipmentFieldSection[] = details.map((detail, i) => ({
        collectionDetail: detail,
        existingInstance: dto.instances?.find(
          instance => instance.collection.id === sorted[i].collection.id,
        ),
        expanded: false,
      }));
      this.sections.set(sections);
    } catch {
      this.toastrService.danger('Felder konnten nicht geladen werden.', 'Fehler');
    } finally {
      this.loading$.next(false);
    }
  }

  toggleExpand(index: number): void {
    this.sections.update(sections =>
      sections.map((s, i) => i === index ? { ...s, expanded: !s.expanded } : s),
    );
  }

  expandAll(): void {
    this.sections.update(sections => sections.map(s => ({ ...s, expanded: true })));
  }

  collapseAll(): void {
    this.sections.update(sections => sections.map(s => ({ ...s, expanded: false })));
  }

  onEdit(): void {
    this.isEditing.set(true);
  }

  async cancel(): Promise<void> {
    this.isEditing.set(false);
    if (this.buildingEquipmentId) {
      await this.loadData(this.buildingEquipmentId);
    }
  }

  async submit(): Promise<void> {
    const forms = this.customForms.toArray();
    const validation = validateCustomForms(forms);
    if (validation.status === 'not-ready') {
      this.toastrService.danger('Formular ist noch nicht bereit.', 'Fehler');
      return;
    }
    if (validation.status === 'invalid') {
      this.toastrService.danger(validation.message, validation.title);
      return;
    }
    try {
      this.saving$.next(true);
      const mergeDto: BuildingEquipmentMergeDto = {
        id: this.buildingEquipmentId!,
        instances: forms.map(form => form.getMergeDto()),
      };
      await firstValueFrom(this.buildingEquipmentService.merge(mergeDto));
      this.isEditing.set(false);
      this.toastrService.success('Felder gespeichert', 'Erfolg');
      await this.loadData(this.buildingEquipmentId!);
    } catch {
      this.toastrService.danger('Felder konnten nicht gespeichert werden.', 'Fehler');
    } finally {
      this.saving$.next(false);
    }
  }

  hasPendingChanges(): boolean {
    return this.isEditing();
  }

  getHeaderBg(color: string | undefined): string {
    if (!color || color.length < 7) return '';
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, 0.12)`;
  }
}
